import { io, Socket } from 'socket.io-client';
import { showCustomToast } from '../components/common/CustomToast';
import { notificationService } from './NotificationService';

interface DepositNotification {
  amount: number;
  newBalance: number;
  transaction: {
    id: string;
    amount: number;
    type: string;
    status: string;
    createdAt: string;
  };
}

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private connected = false;
  private currentUserId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private balanceUpdateCallbacks: ((newBalance: number) => void)[] = [];
  private chatMessageCallbacks: ((message: any) => void)[] = [];
  private chatMessageDeletionCallbacks: ((data: any) => void)[] = [];
  private onlineCountCallbacks: ((count: number) => void)[] = [];
  private coinflipUpdateCallbacks: ((data: any) => void)[] = [];

  private constructor() {}

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  connect(userId?: string): void {
    if (this.socket && this.connected) {
      return;
    }

    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    this.socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      forceNew: false
    });

    this.socket.on('connect', () => {
      this.connected = true;
      this.reconnectAttempts = 0;
      
      // Join user-specific room if userId provided
      if (userId) {
        this.joinUserRoom(userId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      if (reason === 'io server disconnect') {
        // Server forcefully disconnected, try to reconnect
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      this.connected = false;
      // Socket.io will automatically try to reconnect
    });

    // Listen for deposit confirmations
    this.socket.on('deposit-confirmed', (data: DepositNotification) => {
      // data.amount is already in SOL from the backend
      const amountInSOL = data.amount;
      notificationService.addNotification({
        type: 'deposit',
        title: 'Deposit Confirmed',
        message: `+${amountInSOL.toFixed(9)} SOL received`,
        amount: amountInSOL
      });

      // Also show toast for immediate feedback
      showCustomToast.success(
        `Deposit confirmed! +${amountInSOL.toFixed(9)} SOL`,
        5000
      );
      
      // Update balance in UI
      this.notifyBalanceUpdate(data.newBalance);
    });

    // Listen for simple balance updates (lamports as number)
    this.socket.on('balance-update', (balanceInLamports: number) => {
      // Update balance in UI without toast
      this.notifyBalanceUpdate(balanceInLamports);
    });

    // Listen for game creation notifications
    this.socket.on('game-created', (data: { gameType: string; amount: number; gameId: string }) => {
      if (data.gameType === 'coinflip') {
        showCustomToast.success(
          `Coinflip game created! -${data.amount.toFixed(3)} SOL deducted`,
          3000
        );
      }
    });

    // Listen for chat messages
    this.socket.on('chat-message', (message: any) => {
      this.notifyChatMessage(message);
    });

    // Listen for chat message deletions
    this.socket.on('chat-message-deleted', (data: any) => {
      this.notifyChatMessageDeletion(data);
    });

    // Listen for online count updates
    this.socket.on('online-count', (count: number) => {
      this.notifyOnlineCount(count);
    });

    // Listen for coinflip game updates
    this.socket.on('new-coinflip-game', (data: any) => {
      this.notifyCoinflipUpdate({ type: 'new-game', ...data });
    });

    this.socket.on('coinflip-game-completed', (data: any) => {
      this.notifyCoinflipUpdate({ type: 'game-completed', ...data });
    });
  }

  joinUserRoom(userId: string): void {
    if (this.socket && this.connected) {
      this.currentUserId = userId;
      this.socket.emit('join-user-room', userId);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.currentUserId = null;
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  // Join coinflip room
  joinCoinflipRoom(): void {
    if (this.socket && this.connected) {
      this.socket.emit('join-coinflip');
    }
  }

  // Leave coinflip room
  leaveCoinflipRoom(): void {
    if (this.socket && this.connected) {
      this.socket.emit('leave-coinflip');
    }
  }

  // Join chat room
  joinChatRoom(): void {
    if (this.socket && this.connected) {
      this.socket.emit('join-chat');
    }
  }

  // Leave chat room
  leaveChatRoom(): void {
    if (this.socket && this.connected) {
      this.socket.emit('leave-chat');
    }
  }

  // Balance update subscription system
  subscribeToBalanceUpdates(callback: (newBalance: number) => void): () => void {
    this.balanceUpdateCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.balanceUpdateCallbacks = this.balanceUpdateCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyBalanceUpdate(newBalance: number): void {
    this.balanceUpdateCallbacks.forEach(callback => {
      try {
        callback(newBalance);
      } catch (error) {
        console.error('Error in balance update callback:', error);
      }
    });
  }

  // Manually trigger balance update (for other components to use)
  updateBalance(newBalance: number): void {
    this.notifyBalanceUpdate(newBalance);
  }

  // Chat message subscription system
  subscribeToChatMessages(callback: (message: any) => void): () => void {
    this.chatMessageCallbacks.push(callback);
    
    return () => {
      this.chatMessageCallbacks = this.chatMessageCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyChatMessage(message: any): void {
    this.chatMessageCallbacks.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('Error in chat message callback:', error);
      }
    });
  }

  // Chat message deletion subscription system
  subscribeToChatMessageDeletions(callback: (data: any) => void): () => void {
    this.chatMessageDeletionCallbacks.push(callback);
    
    return () => {
      this.chatMessageDeletionCallbacks = this.chatMessageDeletionCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyChatMessageDeletion(data: any): void {
    this.chatMessageDeletionCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in chat message deletion callback:', error);
      }
    });
  }

  // Online count subscription system
  subscribeToOnlineCount(callback: (count: number) => void): () => void {
    this.onlineCountCallbacks.push(callback);
    
    return () => {
      this.onlineCountCallbacks = this.onlineCountCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyOnlineCount(count: number): void {
    this.onlineCountCallbacks.forEach(callback => {
      try {
        callback(count);
      } catch (error) {
        console.error('Error in online count callback:', error);
      }
    });
  }

  // Coinflip update subscription system
  subscribeToCoinflipUpdates(callback: (data: any) => void): () => void {
    this.coinflipUpdateCallbacks.push(callback);
    
    return () => {
      this.coinflipUpdateCallbacks = this.coinflipUpdateCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyCoinflipUpdate(data: any): void {
    this.coinflipUpdateCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in coinflip update callback:', error);
      }
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      if (!this.connected && this.socket) {
        console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.socket.connect();
      }
    }, delay);
  }
}

export default SocketService;

// Export singleton instance
export const socketService = SocketService.getInstance();